import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BadgeCheck, Loader2, ShieldAlert, ShieldX } from 'lucide-react'
import { cn } from '@/lib/utils'
import { fmtDateTime } from '@/shared/lib/format'
import { verifyCertificate, type CertificateVerification } from './verifyApi'

/**
 * The public certificate check — a page with no login and no shell.
 *
 * ## Why it is public
 *
 * The people who most need to check a nomination certificate are exactly the people who have
 * no account here: a returning officer at a commission desk, a journalist, a rival campaign.
 * Putting this behind a sign-in would mean the document can only be verified by the
 * organisation that issued it, which is not verification.
 *
 * ## Three answers, one shape
 *
 * Valid, revoked and unknown all render the same way with a different verdict, so the page
 * never implies that a withdrawn certificate is a forgery — those are very different things
 * to tell somebody holding a piece of paper.
 */
export function VerifyPage() {
  const [params, setParams] = useSearchParams()
  const [reference, setReference] = useState(params.get('ref') ?? '')
  const [result, setResult] = useState<CertificateVerification | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const check = async (value: string) => {
    if (!value.trim()) return
    setBusy(true)
    try {
      setError(null)
      setResult(null)
      setParams({ ref: value.trim() }, { replace: true })
      setResult(await verifyCertificate(value))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Please try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="min-h-dvh bg-background text-foreground">
      <div className="w-full max-w-lg mx-auto px-4 py-10 space-y-6">
        <div>
          <h1 className="text-erp-lg font-semibold">Verify a nomination certificate</h1>
          <p className="text-erp-sm text-muted-foreground mt-1">
            Type the reference printed on the certificate.
          </p>
        </div>

        <form className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); void check(reference) }}>
          <input value={reference} onChange={(e) => setReference(e.target.value)}
            className="erp-input h-12 flex-1 text-erp-base font-mono tracking-wide"
            placeholder="NOM-MCA-…" aria-label="Certificate reference" autoFocus />
          <button type="submit" disabled={busy || !reference.trim()}
            className={cn('erp-btn-primary h-12 px-4 text-erp-base inline-flex items-center gap-2',
              (busy || !reference.trim()) && 'opacity-50')}>
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} Check
          </button>
        </form>

        {error && (
          <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-erp-sm
                        text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300">
            {error}
          </p>
        )}

        {result && <Verdict result={result} />}
      </div>
    </div>
  )
}

function Verdict({ result }: { result: CertificateVerification }) {
  const tone = {
    VALID: {
      icon: BadgeCheck,
      title: 'This certificate is genuine',
      cls: 'border-emerald-300 bg-emerald-50 text-emerald-900 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200',
    },
    REVOKED: {
      icon: ShieldAlert,
      // NOT "fake". A withdrawn certificate was really issued, and saying otherwise accuses
      // the holder of forgery for something the party did.
      title: 'This certificate was issued, then withdrawn',
      cls: 'border-amber-300 bg-amber-50 text-amber-900 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-200',
    },
    UNKNOWN: {
      icon: ShieldX,
      title: 'No certificate has that reference',
      cls: 'border-red-300 bg-red-50 text-red-900 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-200',
    },
  }[result.status]
  const Icon = tone.icon

  return (
    <div className={cn('rounded-lg border-2 px-4 py-4 space-y-3', tone.cls)}>
      <div className="flex items-start gap-2">
        <Icon className="h-6 w-6 shrink-0" />
        <span className="text-erp-base font-semibold">{tone.title}</span>
      </div>

      {result.status === 'UNKNOWN' ? (
        <p className="text-erp-sm">
          Check the reference for typing mistakes. If it is correct, this document was not
          issued by this system.
        </p>
      ) : (
        <dl className="text-erp-sm space-y-1.5">
          <Row label="Candidate" value={result.aspirantName} />
          <Row label="Party" value={result.partyName} />
          <Row label="Seat" value={result.seatTypeCode} />
          <Row label="Place" value={result.unitName} />
          <Row label="Contest" value={result.contestTitle} />
          <Row label="Issued"
            value={result.issuedAt ? fmtDateTime(result.issuedAt) : null} />
          {result.revokedAt && (
            <>
              <Row label="Withdrawn" value={fmtDateTime(result.revokedAt)} />
              <Row label="Reason" value={result.revokeReason} />
            </>
          )}
          <Row label="Reference" value={result.reference} mono />
        </dl>
      )}
    </div>
  )
}

function Row({ label, value, mono }: {
  label: string; value: string | null; mono?: boolean
}) {
  if (!value) return null
  return (
    <div className="flex gap-2">
      <dt className="w-24 shrink-0 opacity-75">{label}</dt>
      <dd className={cn('font-medium', mono && 'font-mono text-erp-xs')}>{value}</dd>
    </div>
  )
}
